import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, FileText, MessageSquare, UserPlus, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

const EVENT_ICONS = {
  transaction_created: CheckCircle2,
  stage_changed: ArrowRight,
  document_uploaded: FileText,
  chat_created: MessageSquare,
  participant_added: UserPlus,
  default: AlertCircle
};

const EVENT_COLORS = {
  transaction_created: "bg-green-500",
  stage_changed: "bg-blue-500",
  document_uploaded: "bg-purple-500",
  chat_created: "bg-amber-500",
  participant_added: "bg-cyan-500",
  default: "bg-muted-foreground"
};

export const TimelinePanel = ({ transactionId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!transactionId) return;
    fetchTimeline();

    // Subscribe to new timeline events
    const channel = supabase
      .channel(`timeline:${transactionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'timeline_events',
          filter: `transaction_id=eq.${transactionId}`
        },
        async (payload) => {
          const { data } = await supabase
            .from('timeline_events')
            .select(`
              *,
              actor:actor_id (
                full_name,
                email
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setEvents(prev => [data, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [transactionId]);

  const fetchTimeline = async () => {
    try {
      const { data, error } = await supabase
        .from('timeline_events')
        .select(`
          *,
          actor:actor_id (
            full_name,
            email
          )
        `)
        .eq('transaction_id', transactionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let dateStr;
    if (date.toDateString() === today.toDateString()) {
      dateStr = "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateStr = "Yesterday";
    } else {
      dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });

    return { date: dateStr, time: timeStr };
  };

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const { date } = formatDateTime(event.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(event);
    return groups;
  }, {});

  return (
    <Card className="border-border/60 shadow-soft" data-testid="timeline-panel">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Live Timeline
        </CardTitle>
        <CardDescription className="text-sm">
          Track all activity and updates for this transaction
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">
              No activity yet. Events will appear here as they happen.
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-8">
              {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                <div key={date}>
                  <div className="sticky top-0 bg-card z-10 py-2">
                    <span className="text-sm font-medium text-muted-foreground">{date}</span>
                  </div>
                  <div className="space-y-4 relative">
                    {/* Timeline Line */}
                    <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border"></div>
                    
                    {dateEvents.map((event, index) => {
                      const IconComponent = EVENT_ICONS[event.event_type] || EVENT_ICONS.default;
                      const colorClass = EVENT_COLORS[event.event_type] || EVENT_COLORS.default;
                      const { time } = formatDateTime(event.created_at);
                      const actorName = event.actor?.full_name || event.actor?.email || 'System';
                      
                      return (
                        <div
                          key={event.id}
                          className="relative flex gap-4 pl-10 animate-fadeIn"
                          style={{ animationDelay: `${index * 50}ms` }}
                          data-testid={`timeline-event-${event.id}`}
                        >
                          {/* Icon */}
                          <div className={`absolute left-0 w-7 h-7 rounded-full ${colorClass} flex items-center justify-center text-white z-10`}>
                            <IconComponent className="w-3.5 h-3.5" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 bg-muted/30 rounded-md p-3.5 border border-border/50">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-medium text-foreground">{event.event_text}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  by {actorName}
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {time}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
